import { useState } from 'react';
import { Alert } from '@/components/alert';
import { Button } from '@/components/button';
import type { Locale } from '@/lib/locale';
import { consentIsIncremental } from '@/lib/consent-scopes';
import { scopeDescription, type Copy } from '@/lib/messages';

interface ConsentStepProps {
  copy: Copy;
  locale: Locale;
  scopes: string[];
  /** Already held by an earlier consent — shown, never re-asked. */
  grantedScopes: string[];
  pending: boolean;
  error: string | null;
  onDecide: (decision: 'allow' | 'deny') => void;
}

/** Scopes that grant an ability rather than reveal data. */
const CAPABILITY_SCOPES = new Set(['offline_access']);

function ScopeList({
  label,
  locale,
  muted = false,
  children,
}: {
  label: string;
  locale: Locale;
  /** Quieter treatment for what is being shown rather than asked. */
  muted?: boolean;
  children: string[];
}) {
  return (
    <div>
      <p
        className={
          muted
            ? 'text-sm font-medium text-buds-muted'
            : 'text-sm font-medium text-buds-dark'
        }
      >
        {label}
      </p>
      <ul className="mt-3 space-y-2">
        {children.map((scope) => (
          <li
            key={scope}
            className="flex gap-2 text-sm leading-relaxed text-buds-muted"
          >
            <span
              aria-hidden="true"
              className={muted ? 'text-buds-muted' : 'text-buds-dark'}
            >
              {muted ? '✓' : '•'}
            </span>
            {scopeDescription(scope, locale)}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ConsentStep({
  copy,
  locale,
  scopes,
  grantedScopes,
  pending,
  error,
  onDecide,
}: ConsentStepProps) {
  const dataScopes = scopes.filter((scope) => !CAPABILITY_SCOPES.has(scope));
  const capabilityScopes = scopes.filter((scope) =>
    CAPABILITY_SCOPES.has(scope),
  );
  // The two lists never claim the same scope twice: whatever the prompt
  // asks for renders in the main list, and only the REST of the grant lands
  // in the muted one.
  const asked = new Set(scopes);
  const alreadyGranted = grantedScopes.filter((scope) => !asked.has(scope));
  // "New —" wording only when something genuinely is: a prior grant exists
  // AND this ask includes a scope it does not cover. A re-confirmation (the
  // ask fully covered — every native login, or a forced prompt=consent)
  // keeps the plain headings it always had.
  const returning = consentIsIncremental(scopes, grantedScopes);
  // Which button was actually pressed. A single global `pending` used to put
  // the spinner on Allow whichever control the user tapped — Deny made Allow
  // spin for the whole round trip, which reads as "it is allowing anyway".
  const [decision, setDecision] = useState<'allow' | 'deny' | null>(null);
  const decide = (value: 'allow' | 'deny') => {
    setDecision(value);
    onDecide(value);
  };
  return (
    <div className="space-y-5">
      {error ? <Alert tone="error">{error}</Alert> : null}

      {/* Data and capability scopes are different promises and get different
          headings: offline_access is not something the client "sees", it is
          continued access after the user leaves — listing it under "see"
          misdescribed the single most consequential thing being granted. */}
      {dataScopes.length > 0 ? (
        <ScopeList
          label={
            returning ? copy.consentNewScopesLabel : copy.consentScopesLabel
          }
          locale={locale}
        >
          {dataScopes}
        </ScopeList>
      ) : null}
      {capabilityScopes.length > 0 ? (
        <ScopeList
          label={
            returning
              ? copy.consentNewCapabilitiesLabel
              : copy.consentCapabilitiesLabel
          }
          locale={locale}
        >
          {capabilityScopes}
        </ScopeList>
      ) : null}

      {/* Last, and quieter: this is context for the decision, not part of it.
          Putting it first would bury what the user is actually being asked.
          Gated on its own content, not on `returning` — a re-confirmation
          asking for a subset still shows what else stays in force. */}
      {alreadyGranted.length > 0 ? (
        <ScopeList
          label={copy.consentAlreadyGrantedLabel}
          locale={locale}
          muted
        >
          {alreadyGranted}
        </ScopeList>
      ) : null}

      {/* Deny is a real answer, not an escape hatch: it gets a proper button
          so refusing is exactly as easy as agreeing. */}
      <div className="space-y-3">
        <Button
          type="button"
          loading={pending && decision === 'allow'}
          disabled={pending}
          onClick={() => decide('allow')}
        >
          {copy.consentAllow}
        </Button>
        <Button
          type="button"
          variant="outline"
          loading={pending && decision === 'deny'}
          disabled={pending}
          onClick={() => decide('deny')}
        >
          {copy.consentDeny}
        </Button>
      </div>
    </div>
  );
}
